import { Mail, MapPin, Phone, UserRound } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export interface BillingContactCardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Card heading. Default "Billing contact". */
  title?: React.ReactNode;
  /** Contact's display name. */
  contactName: React.ReactNode;
  /** Contact role/title (e.g. "Owner", "Office Manager"). */
  contactRole?: React.ReactNode;
  /** Phone number (or a link node wrapping the number). */
  phone?: React.ReactNode;
  /** Email (or a link node wrapping the address). */
  email?: React.ReactNode;
  /** Address line(s) — pass as a single node, or two lines via children. */
  address?: React.ReactNode;
  /** Optional supplementary content rendered below the contact rows. */
  children?: React.ReactNode;
  /**
   * Card chrome (border + surface + padding). Default `true`. Set `false` when
   * the card sits inside another section surface and would read as a
   * card-in-a-card — it then renders flush, and the parent supplies dividers.
   */
  bordered?: boolean;
}

/**
 * Name + phone + email + address card used inside a billing-overview section
 * to surface the human who owns the bill. Presentational — values are passed
 * pre-formatted; phone numbers and emails are not made clickable by default.
 */
export const BillingContactCard = React.forwardRef<HTMLDivElement, BillingContactCardProps>(
  function BillingContactCard(
    {
      address,
      bordered = true,
      children,
      className,
      contactName,
      contactRole,
      email,
      phone,
      title = "Billing contact",
      ...props
    },
    ref,
  ) {
    const isMinimal = !contactRole;

    return (
      <div
        ref={ref}
        className={cx(
          "cw-v2-billing-contact-card",
          isMinimal && "cw-v2-billing-contact-card--minimal",
          !bordered && "cw-v2-billing-contact-card--flush",
          className,
        )}
        {...props}
      >
        <div className="cw-v2-billing-contact-card__header">
          <span className="cw-v2-billing-contact-card__eyebrow">{title}</span>
        </div>
        <div className="cw-v2-billing-contact-card__identity">
          <span aria-hidden className="cw-v2-billing-contact-card__icon">
            <UserRound size={18} />
          </span>
          <div className="cw-v2-billing-contact-card__identity-body">
            <span className="cw-v2-billing-contact-card__name">{contactName}</span>
            {contactRole ? (
              <span className="cw-v2-billing-contact-card__role">{contactRole}</span>
            ) : null}
          </div>
        </div>
        {phone || email || address ? (
          <ul className="cw-v2-billing-contact-card__rows">
            {phone ? (
              <li className="cw-v2-billing-contact-card__row">
                <Phone aria-hidden className="cw-v2-billing-contact-card__row-icon" size={16} />
                <span>{phone}</span>
              </li>
            ) : null}
            {email ? (
              <li className="cw-v2-billing-contact-card__row">
                <Mail aria-hidden className="cw-v2-billing-contact-card__row-icon" size={16} />
                <span>{email}</span>
              </li>
            ) : null}
            {address ? (
              <li className="cw-v2-billing-contact-card__row">
                <MapPin aria-hidden className="cw-v2-billing-contact-card__row-icon" size={16} />
                <span>{address}</span>
              </li>
            ) : null}
          </ul>
        ) : null}
        {children ? <div className="cw-v2-billing-contact-card__footer">{children}</div> : null}
      </div>
    );
  },
);
BillingContactCard.displayName = "BillingContactCard";
